import { useState } from 'react'
import { useAdminProfiles, useExportProfiles } from '@/hooks/useAdminProfiles'
import { useAdminTenants } from '@/hooks/useAdminTenants'
import ProfileCard from '@/components/admin/ProfileCard'

export default function ProfileBrowser() {
  const [search, setSearch] = useState('')
  const [tenantFilter, setTenantFilter] = useState<string>('')
  const [coverageFilter, setCoverageFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedProfiles, setSelectedProfiles] = useState<Set<string>>(new Set())

  const { data, isLoading, error } = useAdminProfiles({
    search: search || undefined,
    tenant_id: tenantFilter || undefined,
    coverage_level: coverageFilter || undefined,
    is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
  })

  const { data: tenantsData } = useAdminTenants({ limit: 200 })
  const exportMutation = useExportProfiles()

  const profiles = data?.profiles || []
  const tenants = tenantsData?.tenants || []

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProfiles(new Set(profiles.map((p) => p.profile_id)))
    } else {
      setSelectedProfiles(new Set())
    }
  }

  const handleSelectProfile = (profileId: string, checked: boolean) => {
    const newSet = new Set(selectedProfiles)
    if (checked) {
      newSet.add(profileId)
    } else {
      newSet.delete(profileId)
    }
    setSelectedProfiles(newSet)
  }

  const handleExport = async () => {
    if (selectedProfiles.size === 0) {
      alert('Please select at least one profile to export')
      return
    }

    try {
      const blob = await exportMutation.mutateAsync(Array.from(selectedProfiles))

      // Download file
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `personic-profiles-${new Date().toISOString()}.json`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      // Clear selection
      setSelectedProfiles(new Set())
    } catch (err: any) {
      alert('Export failed: ' + (err.response?.data?.detail || err.message))
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Personic AI Profile Browser</h1>
        <p className="text-gray-600 mt-1">
          Search and browse profiles across all tenants
        </p>
      </div>

      {/* Filters */}
      <div className="card p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Canonical ID or Roscoe ID..."
              className="input"
            />
          </div>

          {/* Tenant Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tenant
            </label>
            <select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              className="input"
            >
              <option value="">All Tenants</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>

          {/* Coverage Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coverage Level
            </label>
            <select
              value={coverageFilter}
              onChange={(e) => setCoverageFilter(e.target.value)}
              className="input"
            >
              <option value="">All Levels</option>
              <option value="minimal">Minimal</option>
              <option value="emerging">Emerging</option>
              <option value="established">Established</option>
              <option value="mature">Mature</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      {selectedProfiles.size > 0 && (
        <div className="card p-4 bg-personic-50 border-personic-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              <strong>{selectedProfiles.size}</strong> profile(s) selected
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedProfiles(new Set())}
                className="btn-secondary"
              >
                Clear Selection
              </button>
              <button
                onClick={handleExport}
                disabled={exportMutation.isPending}
                className="btn-primary"
              >
                {exportMutation.isPending ? 'Exporting...' : 'Export Selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Profiles ({data?.total || 0})
            </h3>

            {profiles.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedProfiles.size === profiles.length && profiles.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                Select All
              </label>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-500">Loading profiles...</div>
          </div>
        ) : error ? (
          <div className="px-6 py-12">
            <div className="bg-red-50 border border-red-200 rounded p-4 text-red-700">
              Failed to load profiles.
            </div>
          </div>
        ) : profiles.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No profiles found matching your filters.
          </div>
        ) : (
          <div className="p-6 space-y-3">
            {profiles.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                selected={selectedProfiles.has(profile.profile_id)}
                onSelect={(checked) => handleSelectProfile(profile.profile_id, checked)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
